import { createContext, ReactNode, useContext, useEffect, useLayoutEffect, useMemo, useReducer, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { defaultLocale, supportedLocales } from '@/shared/i18n/config';
import { setBrowserBeforePopState } from './browserHistory';
import { localizePathname, splitLocale } from './localePath';
import { queryFromRouteLocation, resolveClientRouteLocation } from './routeState';
import { readBrowserLocation, resolvePageResetState, subscribeToSamePageHistoryChanges } from './samePageHistory';
import { ClientRouterPopStateDecision } from './types';
import type { ClientRouter, NavigationOptions, RouteDefinition, RouterQuery, UrlObject } from './types';

type Listener = (...args: any[]) => void;

class RouterEvents {
	private listeners = new Map<string, Set<Listener>>();

	on(event: string, listener: Listener): void {
		const listeners = this.listeners.get(event) ?? new Set<Listener>();
		listeners.add(listener);
		this.listeners.set(event, listeners);
	}

	off(event: string, listener: Listener): void {
		this.listeners.get(event)?.delete(listener);
	}

	emit(event: string, ...args: any[]): void {
		this.listeners.get(event)?.forEach((listener) => listener(...args));
	}
}

export const routerEvents = new RouterEvents();

const RouterContext = createContext<ClientRouter | null>(null);

function fillDynamicPath(pathname: string, query: RouterQuery): { pathname: string; used: Set<string> } {
	const used = new Set<string>();
	let result = pathname;

	result = result.replace(/\/\[\[\.\.\.(.+?)\]\]/g, (_match, key: string) => {
		used.add(key);
		const value = query[key];
		if (value === undefined) return '';
		return `/${(Array.isArray(value) ? value : [value]).map((part) => encodeURIComponent(part)).join('/')}`;
	});
	result = result.replace(/\[\.\.\.(.+?)\]/g, (_match, key: string) => {
		used.add(key);
		const value = query[key];
		return (Array.isArray(value) ? value : value ? [value] : []).map((part) => encodeURIComponent(part)).join('/');
	});
	result = result.replace(/\[(.+?)\]/g, (_match, key: string) => {
		used.add(key);
		const value = query[key];
		return encodeURIComponent(Array.isArray(value) ? (value[0] ?? '') : (value ?? ''));
	});

	return { pathname: result || '/', used };
}

function isExternalUrl(url: string): boolean {
	return /^(?:[a-z]+:)?\/\//i.test(url) || /^(?:mailto|tel):/i.test(url);
}

interface ClientRouterProviderProps {
	children: ReactNode;
	params?: Readonly<Record<string, string | undefined>>;
	route: RouteDefinition;
	routes?: readonly RouteDefinition[];
}

export function ClientRouterProvider({ children, params = {}, route, routes = [route] }: ClientRouterProviderProps) {
	const location = useLocation();
	const navigate = useNavigate();
	const [, refreshBrowserLocation] = useReducer((revision: number) => revision + 1, 0);
	const browserLocation = readBrowserLocation(location);
	const browserPath = `${browserLocation.pathname}${browserLocation.search}${browserLocation.hash}`;
	const localizedLocation = splitLocale(browserLocation.pathname);
	// Same-page editors use native history so their mounted state survives. React Router therefore
	// keeps its previous match; rematch the real browser path so pathname and dynamic query params
	// move together with asPath.
	const routeLocation = useMemo(
		() => resolveClientRouteLocation(localizedLocation.pathname, { params, route }, routes),
		[localizedLocation.pathname, params, route, routes]
	);
	const query = useMemo(
		() => queryFromRouteLocation(browserLocation.search, routeLocation.params, routeLocation.route),
		[browserLocation.search, routeLocation]
	);
	const stateRef = useRef({ navigate, query, locale: localizedLocation.locale, pathname: localizedLocation.pathname });
	stateRef.current = { navigate, query, locale: localizedLocation.locale, pathname: localizedLocation.pathname };
	const beforePopRef = useRef<ClientRouter['beforePopState'] extends (callback: infer C) => void ? C : never>(
		() => ClientRouterPopStateDecision.Continue
	);
	const pageResetStateRef = useRef({ browserPath, locationKey: location.key, resetKey: 0 });
	pageResetStateRef.current = resolvePageResetState(pageResetStateRef.current, browserPath, location.key);

	const routerRef = useRef<ClientRouter>();
	if (!routerRef.current) {
		const resolveHref = (url: string | UrlObject, options?: NavigationOptions): string => {
			if (typeof url === 'string' && isExternalUrl(url)) return url;
			const object = typeof url === 'string' ? { pathname: url } : url;
			const [rawPathname, inlineSearch = ''] = (object.pathname ?? stateRef.current.pathname).split('?');
			const mergedQuery = { ...stateRef.current.query, ...object.query };
			const filled = fillDynamicPath(rawPathname, mergedQuery);
			const search = new URLSearchParams(inlineSearch);
			for (const [key, value] of Object.entries(object.query ?? {})) {
				if (!filled.used.has(key) && value !== undefined) {
					search.delete(key);
					for (const item of Array.isArray(value) ? value : [value]) search.append(key, item);
				}
			}

			const requestedLocale = options?.locale === false ? defaultLocale : (options?.locale ?? stateRef.current.locale);
			const searchString = search.toString();
			const localizedPathname = localizePathname(filled.pathname, requestedLocale);
			return `${localizedPathname}${searchString ? `?${searchString}` : ''}${object.hash ?? ''}`;
		};

		const changeRoute = (
			method: 'push' | 'replace',
			url: string | UrlObject,
			as?: string,
			options?: NavigationOptions
		): Promise<boolean> => {
			const destination = resolveHref(as ?? url, options);
			routerEvents.emit('routeChangeStart', destination, options ?? {});
			try {
				stateRef.current.navigate(destination, { replace: method === 'replace' });
				return Promise.resolve(true);
			} catch (error) {
				routerEvents.emit('routeChangeError', error, destination, options ?? {});
				return Promise.resolve(false);
			}
		};

		routerRef.current = {
			asPath: '/',
			basePath: '',
			events: routerEvents,
			isReady: true,
			locale: defaultLocale,
			locales: [...supportedLocales],
			pageResetKey: 0,
			pathname: '/',
			query: {},
			back: () => window.history.back(),
			beforePopState: (callback) => {
				beforePopRef.current = callback;
			},
			prefetch: () => Promise.resolve(),
			push: (url, as, options) => changeRoute('push', url, as, options),
			reload: () => window.location.reload(),
			replace: (url, as, options) => changeRoute('replace', url, as, options),
			resolveHref
		};
	}

	const router = routerRef.current;
	const currentAsPath = `${localizedLocation.pathname}${browserLocation.search}${browserLocation.hash}`;
	router.asPath = currentAsPath;
	router.locale = localizedLocation.locale;
	router.pageResetKey = pageResetStateRef.current.resetKey;
	router.pathname = routeLocation.route.routePattern;
	router.query = query;
	// The imperative methods stay stable, but route fields must be published through
	// a new context value. Several route modules intentionally share the same memoized
	// page component (for example the Automation Rules list and editor routes); mutating
	// one router object in place lets React.memo skip those consumers after navigation,
	// leaving the previous surface rendered under the destination URL.
	const routerValue = useMemo<ClientRouter>(
		() => ({ ...router }),
		[browserLocation.hash, browserLocation.pathname, browserLocation.search, location.key, query, routeLocation.route]
	);

	useEffect(() => {
		routerEvents.emit('routeChangeComplete', currentAsPath, {});
	}, [currentAsPath, location.key]);

	useLayoutEffect(() => subscribeToSamePageHistoryChanges(refreshBrowserLocation), []);

	useLayoutEffect(() => {
		return setBrowserBeforePopState((state) => beforePopRef.current(state));
	}, []);

	return <RouterContext.Provider value={routerValue}>{children}</RouterContext.Provider>;
}

export function useRouter(): ClientRouter {
	const router = useContext(RouterContext);
	if (!router) throw new Error('useRouter must be used inside ClientRouterProvider');
	return router;
}

export type { ClientRouter } from './types';
